import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import { HiOutlineMenuAlt3, HiOutlineX } from "react-icons/hi";
import { useSelector } from "react-redux";
import NavDropDown from "../DropDown/NavDropDown";
import UserAvatar from "../UserAvatar/UserAvatar";
import { ROUTES } from "../../constants";

const Navbar = ({ user }) => {
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);
  const { isAuthenticated } = useSelector((state) => state.user);
  const { pathname } = useLocation();
  const isAdmin = useSelector((state) => state.user?.user?.role) === "admin";

  useEffect(() => {
    document.body.style.overflow = showNavbar ? "hidden" : "unset";
  }, [showNavbar]);

  // Modern Apple-style Link Stylings
  const getLinkStyle = (path) => {
    const isActive = pathname === path;
    return `relative px-4 py-2 text-sm font-semibold transition-all duration-300 rounded-full flex items-center justify-center
      ${isActive 
        ? "text-blue-600 bg-blue-50/50 shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)]" 
        : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
      }`;
  };

  return (
    <>
      {/* Main Nav Container */}
      <nav className="bg-white/70 sticky top-0 z-[100] border-b border-slate-200/50 backdrop-blur-xl transition-all">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          
          {/* 1. Logo Section (3D hover effect) */}
          <Link to={ROUTES.home} className="flex items-center gap-2 group">
            <div className="relative">
              <img 
                src={logo} 
                alt="CampusIRA" 
                className="h-9 w-auto object-contain transition-transform duration-500 group-hover:rotate-[5deg] group-hover:scale-110" 
              />
            </div>
            <h1 className="text-xl font-black tracking-tighter text-slate-900 hidden sm:block">
              CAMPUS<span className="text-blue-600">IRA</span>
            </h1>
          </Link>

          {/* 2. Desktop Navigation (Professional Naming) */}
          <div className="hidden lg:flex items-center bg-slate-100/30 p-1 rounded-full border border-slate-200/20 backdrop-blur-md">
            <Link to={ROUTES.home} className={getLinkStyle(ROUTES.home)}>Home</Link>
            <Link to={ROUTES.posts} className={getLinkStyle(ROUTES.posts)}>Experiences</Link>
            <Link to={ROUTES.qaPage} className={getLinkStyle(ROUTES.qaPage)}>Q&A</Link>
            <Link to={ROUTES.resources} className={getLinkStyle(ROUTES.resources)}>Library</Link>
            <Link to={ROUTES.jobs} className={getLinkStyle(ROUTES.jobs)}>Job Alerts</Link>
            <Link to={ROUTES.help} className={getLinkStyle(ROUTES.help)}>Feedback</Link>
            {isAdmin && (
              <Link to={ROUTES.adminFeedback} className={getLinkStyle(ROUTES.adminFeedback)}>
                Admin
              </Link>
            )}
          </div>

          {/* 3. Auth Section */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setDropdownVisible(!isDropdownVisible)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all duration-300 ${
                    isDropdownVisible 
                    ? 'bg-white border-blue-200 shadow-lg' 
                    : 'bg-slate-50 border-transparent hover:border-slate-200'
                  }`}
                >
                  <UserAvatar src={user?.profileImage} name={user?.name} size={28} />
                  <span className="text-sm font-bold text-slate-700">{user?.name}</span>
                </button>
                {isDropdownVisible && <NavDropDown toggleDropdown={() => setDropdownVisible(false)} />}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link
                  to={ROUTES.login}
                  className="px-4 py-2 text-sm font-bold text-slate-600 hover:text-blue-600 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to={ROUTES.login}
                  className="bg-blue-600 text-white px-5 py-2 rounded-full font-bold text-sm shadow-[0_4px_14px_rgba(37,99,235,0.3)] hover:shadow-blue-400/40 hover:scale-[1.02] active:scale-95 transition-all"
                >
                  Join Now
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden p-2 text-slate-900 bg-slate-50 rounded-full hover:bg-slate-100 transition-all" 
              onClick={() => setShowNavbar(true)}
            >
              <HiOutlineMenuAlt3 size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* 4. Full Screen Mobile Menu (Refined) */}
      <div 
        className={`fixed inset-0 z-[1000] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          showNavbar ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-white/95 backdrop-blur-3xl" />

        <div className="relative h-full flex flex-col p-8">
          <div className="flex justify-between items-center mb-12">
            <h1 className="text-2xl font-black tracking-tighter text-slate-900">CAMPUS<span className="text-blue-600">IRA</span></h1>
            <button 
              onClick={() => setShowNavbar(false)} 
              className="p-3 bg-slate-100 text-slate-900 rounded-full hover:rotate-90 transition-all duration-300"
            >
              <HiOutlineX size={24} />
            </button>
          </div>

          <div className="flex flex-col gap-8">
            {[
              { label: 'Home', route: ROUTES.home },
              { label: 'Experiences', route: ROUTES.posts },
              { label: 'Q&A', route: ROUTES.qaPage },
              { label: 'Library', route: ROUTES.resources },
              { label: 'Job Alerts', route: ROUTES.jobs },
              { label: 'Feedback', route: ROUTES.help },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.route}
                onClick={() => setShowNavbar(false)}
                className="text-4xl font-black text-slate-900 hover:text-blue-600 hover:translate-x-2 transition-all duration-300"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="mt-auto pt-8 border-t border-slate-100">
            {!isAuthenticated ? (
              <div className="grid grid-cols-2 gap-4">
                <Link to={ROUTES.login} onClick={() => setShowNavbar(false)} className="py-4 text-center font-bold text-slate-600 bg-slate-50 rounded-2xl">Log in</Link>
                <Link to={ROUTES.login} onClick={() => setShowNavbar(false)} className="py-4 text-center font-bold text-white bg-blue-600 rounded-2xl shadow-xl shadow-blue-200">Join Now</Link>
              </div>
            ) : (
              <p className="text-center font-bold text-slate-400">Signed in as {user?.name}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;